import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandName } from '@/helpers/brand-name';
import { modal } from '@/helpers/ui';
import { segment } from '@/segment';
import { useCompanyStore } from '@/stores/company';
import type { ReturnData, TrialTask } from '../types';

const taskSegmentName: Record<string, string> = {
  try_booking_widget: 'try_booking_widget',
  create_appointment: 'make_first_appointment',
  setup_roster: 'fill_opening_hours',
  import_customers: 'upload_client_list',
  setup_company_details: 'fill_company_info',
};

export default (): ReturnData => {
  const { t } = useI18n();
  const { isInsideTestBucket, company, isTreatwellUser } = useCompanyStore();

  const claimable =
    !isTreatwellUser &&
    (company.country === 'be' || isInsideTestBucket('trial_rewards_enabled'));

  const data = computed(() => {
    const { trialDaysRemaining } = useCompanyStore();

    return {
      heading: isTreatwellUser
        ? t('onboarding.intro.title_treatwell')
        : t('onboarding.intro.title', brandName()),
      icon: {
        dark: '/img/onboarding/icon-dark.svg',
        light: '/img/onboarding/icon-light.svg',
      },
      link: {
        routeName: 'subscription',
        text: t('onboarding.new_onboarding.tasks.upgrade'),
      },
      remainingDays: trialDaysRemaining,
    };
  });

  const tasks = ref<TrialTask[]>(
    [
      'create_appointment',
      'try_booking_widget',
      'setup_roster',
      'import_customers',
      'setup_company_details',
    ].map((name) => ({
      claimed: false,
      claimable,
      completed: false,
      label: claimable ? `+1 ${t('onboarding.new_onboarding.days_free')}` : '',
      labelColor: 'primary',
      name,
      text: t(`onboarding.new_onboarding.tasks.${name}`),
      visual: `/img/tasks/${name}.svg`,
    })),
  );

  const { onResult, loading } = useQuery(gql`
    query getOnboardingTasks {
      onboardingTasks {
        all {
          claimedAt
          completedAt
          id
          task
        }
      }
    }
  `);

  onResult(
    ({
      data: {
        onboardingTasks: { all },
      },
    }) => {
      all.forEach((taskData: any) => {
        const task = tasks.value.find((task) => task.name === taskData.task);
        if (task) {
          task.claimed = !!taskData.claimedAt;
          task.completed = !!taskData.completedAt;
          task.label = claimable
            ? taskData.claimedAt
              ? t('onboarding.new_onboarding.days_claimed')
              : `+1 ${t('onboarding.new_onboarding.days_free')}`
            : '';
          task.labelColor = taskData.completedAt ? 'success' : 'primary';
        }
      });
    },
  );

  const claimTask = (taskName: string) => {
    const task = tasks.value.find((task) => task.name === taskName);
    if (!task) {
      return;
    }

    segment.track('onboarding.checklist.trial_day.claim_clicked', {
      item_name: taskSegmentName[taskName],
    });

    const { mutate: updateTask } = useMutation(gql`
      mutation updateOnboardingTask($input: UpdateOnboardingTaskInput!) {
        updateOnboardingTask(input: $input) {
          onboardingTask {
            id
            task
            completedAt
            claimedAt
          }
        }
      }
    `);

    updateTask({
      input: {
        task: task.name.toUpperCase(),
        taskStatus: 'CLAIM',
      },
    }).then(() => {
      const { onResult } = useQuery(gql`
        query getCurrentUser {
          currentUser {
            company {
              trialEndAt
            }
          }
        }
      `);

      onResult(
        ({
          data: {
            currentUser: {
              company: { trialEndAt },
            },
          },
        }) => {
          task.claimed = true;

          if (typeof task.label === 'object') {
            task.label = t('onboarding.new_onboarding.days_claimed');
          }

          const { company } = useCompanyStore();
          company.trialEndAt = trialEndAt;
        },
      );
    });
  };

  const onTaskClick = (taskName: string) => {
    switch (taskName) {
      case 'try_booking_widget':
        segment.track('onboarding.checklist.item.clicked', {
          item_name: taskSegmentName[taskName],
        });
        modal('try-booking-widget');
        break;
      case 'create_appointment':
        segment.track('onboarding.checklist.item.clicked', {
          item_name: taskSegmentName[taskName],
        });
        modal('new-onboarding');
        break;
      case 'setup_roster':
        segment.track('onboarding.checklist.item.clicked', {
          item_name: taskSegmentName[taskName],
        });
        modal('create-roster');
        break;
      case 'import_customers':
        segment.track('onboarding.checklist.item.clicked', {
          item_name: taskSegmentName[taskName],
        });
        modal('import-customers');
        break;
      case 'setup_company_details':
        segment.track('onboarding.checklist.item.clicked', {
          item_name: taskSegmentName[taskName],
        });
        modal('setup-company-info');
        break;
    }
  };

  return {
    data,
    tasks,
    loading,
    claimTask,
    onTaskClick,
  };
};
